import { EditMode } from "@/types/editMode";
import {
    AppSettingType,
    Co2MaterialCategory,
    Co2MaterialLibrary,
    Co2MaterialType,
    DensityType,
    FileParameter,
    ImportCo2MaterialTypesResponse,
    MaterialLibraryType,
    SoftDeleteStatusType,
    TemplateType,
    UpsertCo2MaterialTypeRequest,
    ValidationFailure,
} from "@api/apiClient";
import { useAppSettings } from "@api/appSettings";
import { useCo2MaterialLibraries } from "@api/co2MaterialLibraries";
import { useCo2MaterialTypeOptions, useCo2MaterialTypes, useDeleteCo2MaterialType, useUpsertCo2MaterialType } from "@api/co2MaterialTypes";
import { useUploadTemplate } from "@api/templates";
import DataGrid from "@components/DataGrid/DataGrid";
import { GridToolbarImportButton } from "@components/DataGrid/components/toolbar/GridToolbarImportButton";
import MaterialLibrarySelect from "@components/DataGrid/components/toolbar/MaterialLibrarySelect";
import MultiGridToolbarSearchFilter from "@components/DataGrid/components/toolbar/MultiGridToolbarSearchFilter";
import useAppStore from "@hooks/useAppStore";
import i18n from "@i18n/i18n";
import CancelIcon from "@mui/icons-material/Cancel";
import SuccessIcon from "@mui/icons-material/CheckCircle";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import ErrorIcon from "@mui/icons-material/Error";
import SaveIcon from "@mui/icons-material/Save";
import ResetIcon from "@mui/icons-material/SettingsBackupRestore";
import WarningIcon from "@mui/icons-material/Warning";
import { Alert, AlertTitle, Box, Button, Checkbox, FormControlLabel, Grid, Theme, Tooltip, alpha, darken, lighten, useTheme } from "@mui/material";
import {
    GridActionsCellItem,
    GridColDef,
    GridEditInputCell,
    GridEventListener,
    GridExceljsProcessInput,
    GridPreProcessEditCellProps,
    GridRenderEditCellParams,
    GridRowEditStopReasons,
    GridRowId,
    GridRowModel,
    GridRowModes,
    GridRowModesModel,
    GridSlotsComponentsProps,
    GridSortModel,
    GridToolbarColumnsButton,
    GridToolbarContainer,
    GridToolbarExport,
    GridToolbarFilterButton,
    useGridApiRef,
} from "@mui/x-data-grid-premium";
import { getClientColumnConfig, getClientConfigKey, setMaterialsAsDefault, validateClientConfig } from "@utils/clientConfigUtils";
import _ from "lodash";
import { observer } from "mobx-react";
import { useCallback, useEffect, useMemo, useRef, useState, startTransition } from "react";
import { ErrorBoundary } from "react-error-boundary";
import useResizeObserver from "use-resize-observer";
import DataGridAutocomplete from "./DataGridAutocomplete";

export interface MaterialGridProps {
  gridHeight?: number;
}

const MaterialGrid = observer((props: MaterialGridProps) => {
  console.log("MaterialGrid", props);
  const appStore = useAppStore();
  const theme = useTheme();
  const [editMode, setEditMode] = useState(EditMode.ReadOnly);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [showErrorMessage, setShowErrorMessage] = useState(false);
  const [showWarningMessage, setShowWarningMessage] = useState(false);
  const [showConfigErrorMessage, setShowConfigErrorMessage] = useState(false);
  const [configErrorMessage, setConfigErrorMessage] = useState<string>("");
  const [importData, setImportData] = useState<FileParameter>();
  const [showArchived, setShowArchived] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [rowModesModel, setRowModesModel] = useState<GridRowModesModel>({});
  const rowModesModelRef = useRef<GridRowModesModel>({});
  const rowsRef = useRef<any[]>([]);
  const [rows, setRows] = useState<any[]>([]);
  const [columnVisibilityModel, setColumnVisibilityModel] = useState<any>({});
  const [sortModel, setSortModel] = useState<GridSortModel>([
    { field: "name", sort: "asc" },
    { field: "category", sort: "asc" },
    { field: "a1A2A3", sort: "asc" },
  ]);
  const [materialLibraryIds, setMaterialLibraryIds] = useState<string[]>([]);

  const [validationErrors, setValidationErrors] = useState<any>({});
  const [validationFailure, setValidationFailure] = useState<ValidationFailure[] | undefined>();
  const materialLibraries = useCo2MaterialLibraries();

  const clientKey = useMemo(() => getClientConfigKey(appStore.applicationSettings), [appStore.applicationSettings]);

  // Determine API parameters: if no libraries selected but libraries exist, fetch all materials
  // Use undefined instead of empty array to prevent 404 errors when no libraries are available
  const apiLibraryIds = materialLibraryIds.length > 0 ? materialLibraryIds : undefined;
  // Only fetch if libraries are available and not still loading
  const fetchData = !materialLibraries.isLoading && materialLibraries.data && materialLibraries.data.length > 0;

  const co2MaterialTypes = useCo2MaterialTypes(apiLibraryIds, showArchived, {
    enabled: fetchData,
  });
  const co2MaterialTypeOptions = useCo2MaterialTypeOptions(apiLibraryIds, showArchived, {
    enabled: fetchData,
  });

  // Use ref to store latest options data for access in render functions
  const co2MaterialTypeOptionsRef = useRef(co2MaterialTypeOptions?.data);
  useEffect(() => {
    co2MaterialTypeOptionsRef.current = co2MaterialTypeOptions?.data;
  }, [co2MaterialTypeOptions?.data]);

  useEffect(() => {
    co2MaterialTypeOptions.data?.co2MaterialCategories?.sort(
      (a: Co2MaterialCategory | undefined, b: Co2MaterialCategory | undefined) => a?.name?.localeCompare(b?.name || "") || 0,
    );
  }, [co2MaterialTypeOptions.data]);

  const upsertCo2MaterialType = useUpsertCo2MaterialType();
  const deleteCo2MaterialType = useDeleteCo2MaterialType();
  const uploadTemplate = useUploadTemplate({
    onSuccess: async (response: ImportCo2MaterialTypesResponse) => {
      console.log("useUploadTemplate onSuccess", response);
      // TODO: must disable EDIT mode when viewing results
      const successRows =
        response.importCo2MaterialTypeResult?.successItems?.map((item, index) => ({ ...item, rowId: index, id: index, co2MaterialTypeId: item.co2MaterialTypeId || index })) || [];
      const failureRows =
        response.importCo2MaterialTypeResult?.failureItems?.map((item, index) => ({
          rowId: index + successRows.length + 1,
          id: index + successRows.length + 1,
          ...item.importCo2MaterialType,
          co2MaterialTypeId: item.importCo2MaterialType?.co2MaterialTypeId || index + successRows.length + 1,
        })) || [];
      const validationerrors = response.validationFailures;
      const newRows = [...successRows, ...failureRows];
      setGridHeight(400);
      setShowSuccessMessage(failureRows.length === 0 && validationerrors != undefined && validationerrors.length == 0);
      setShowErrorMessage(failureRows.length !== 0);
      setShowWarningMessage(failureRows.length === 0 && validationerrors != undefined && validationerrors.length > 0);
      setValidationFailure(validationerrors);
      setRows(newRows);
      startImportMode();
    },
  });
  const apiRef = useGridApiRef();
  const containerRef = useRef(null);
  const alertRef = useRef(null);
  useResizeObserver({
    ref: containerRef,
    onResize: (size) => {
      console.log("containerRef", size);
      setContainerHeight(size.height || 400);
      setGridHeight((size.height || 400) - (alertHeight || 0));
    },
  });
  const { height: alertHeight } = useResizeObserver({
    ref: alertRef,
    onResize: (size) => {
      console.log("alertRef", size);
      setGridHeight(containerHeight - (size.height || 0) - 16);
    },
  });
  const [containerHeight, setContainerHeight] = useState(0);
  const [gridHeight, setGridHeight] = useState(props.gridHeight || 400);
  const [coordinates, setCoordinates] = useState({ rowIndex: 0, colIndex: 0 });
  const localStorageKey = `matGrid`;
  const [densityIndicator] = useState([
    { value: DensityType.Volume, name: i18n.t("material-grid.density-type.volume") },
    { value: DensityType.Area, name: i18n.t("material-grid.density-type.area") },
  ]);
  const startImportMode = () => {
    console.log("startImportMode");

    const defaultImportVisibility = {
      ...columnVisibilityModel,
      rowNum: true,
      importStatus: true,
      importAction: true,
      importStatusMessage: true,
      actions: false, // Hide actions during import
    };
    setColumnVisibilityModel(defaultImportVisibility);
  };

  const endImportMode = async () => {
    console.log("endImportMode");
    setShowSuccessMessage(false);
    setShowErrorMessage(false);
    setShowWarningMessage(false);
    setShowConfigErrorMessage(false);
    setValidationFailure([]);
    setGridHeight(containerHeight - 16);
    setEditMode(EditMode.ReadOnly);

    // Reset column visibility to initial state instead of manually setting each column
    if (defaultColumns.length > 0) {
      const resetColumnVisibility = getInitialColumnVisibilityModel();

      setColumnVisibilityModel(resetColumnVisibility);

      setDisplayColumns(defaultColumns);
    }
  };

  const canEdit = editMode !== EditMode.Import;

  useEffect(() => {
    if (apiRef.current) {
      try {
        apiRef.current.scrollToIndexes(coordinates);
        apiRef.current.setCellFocus(coordinates.rowIndex, "name");
      } catch (error) {
        console.warn("Error setting cell focus:", error);
      }
    }
  }, [apiRef, coordinates]);

  useEffect(() => {
    // Don't call forceExitAllEditModes here - it interferes with DataGrid's internal state management
    // DataGrid will handle mode cleanup when rows change
    // Clear rows only if no libraries are available at all
    if (!(materialLibraries.data?.length > 0)) {
      setRows([]);
      // Clear rowModesModel immediately when rows are cleared to prevent DataGrid errors
      setRowModesModel({});
      rowModesModelRef.current = {};
      return;
    }
    // Handle API errors gracefully
    if (co2MaterialTypes.error) {
      console.error("Error loading material types:", co2MaterialTypes.error);
      setRows([]);
      // Clear rowModesModel immediately when rows are cleared to prevent DataGrid errors
      setRowModesModel({});
      rowModesModelRef.current = {};
      return;
    }

    if (co2MaterialTypes.data) {
      const newRows = co2MaterialTypes.data
        .filter((r: Co2MaterialType) => showArchived || r.deleteStatus === SoftDeleteStatusType.Active)
        .map((r: Co2MaterialType, index: number) => {
          return { ...r, rowId: index, id: index };
        });
      const initialRowModesModel: GridRowModesModel = {};
      newRows.forEach((row: any) => {
        const rowId = row.co2MaterialTypeId;
        if (rowId !== undefined && rowId !== null) {
          initialRowModesModel[rowId] = { mode: GridRowModes.View };
        }
      });
      rowModesModelRef.current = initialRowModesModel;
      // Batch both updates in one transition so the grid never sees a partial state
      startTransition(() => {
        setRows(newRows);
        setRowModesModel(initialRowModesModel);
      });
    } else {
      // Clear rows when data is not available (e.g., during loading)
      setRows([]);
      // Clear rowModesModel immediately when rows are cleared to prevent DataGrid errors
      setRowModesModel({});
      rowModesModelRef.current = {};
    }
  }, [co2MaterialTypes.data, co2MaterialTypes.error, showArchived, materialLibraries.data]);

  useEffect(() => {
    if (apiRef.current) {
      const quickFilterValues = searchText.split(" ").filter((word) => word !== "");
      apiRef.current.setQuickFilterValues(quickFilterValues);
    }
  }, [searchText]);

  // Keep the refs in sync with the state
  useEffect(() => {
    rowModesModelRef.current = rowModesModel;
  }, [rowModesModel]);

  useEffect(() => {
    rowsRef.current = rows;
  }, [rows]);

  // Helper function to safely update rowModesModel
  // Only validates structure, doesn't filter by row existence
  // DataGrid needs to manage rows in transition (new rows, updates, etc.)
  const safeSetRowModesModel = useCallback((newModel: GridRowModesModel) => {
    try {
      if (!newModel || typeof newModel !== 'object') {
        setRowModesModel({});
        rowModesModelRef.current = {};
        return;
      }

      // Only validate structure, don't filter by existence
      // Fix invalid entries but keep all entries
      const cleanedModel: GridRowModesModel = {};
      let hasChanges = false;

      Object.keys(newModel).forEach((rowId) => {
        try {
          const modeEntry = newModel[rowId];
          
          // Validate and fix mode entry structure
          if (!modeEntry || typeof modeEntry !== 'object' || !('mode' in modeEntry)) {
            cleanedModel[rowId] = { mode: GridRowModes.View };
            hasChanges = true;
          } else if (modeEntry.mode !== GridRowModes.View && modeEntry.mode !== GridRowModes.Edit) {
            cleanedModel[rowId] = { mode: GridRowModes.View };
            hasChanges = true;
          } else {
            // Valid entry, keep it
            cleanedModel[rowId] = modeEntry;
          }
        } catch (e) {
          // Fix invalid entries
          cleanedModel[rowId] = { mode: GridRowModes.View };
          hasChanges = true;
        }
      });

      // Only update if there were invalid entries to fix
      if (hasChanges) {
        setRowModesModel(cleanedModel);
        rowModesModelRef.current = cleanedModel;
      } else {
        // No changes needed, set directly
        setRowModesModel(newModel);
        rowModesModelRef.current = newModel;
      }
    } catch (error) {
      console.error("Error in safeSetRowModesModel:", error);
      setRowModesModel({});
      rowModesModelRef.current = {};
    }
  }, []); // No dependencies - uses refs for latest values

  // Clean up rowModesModel to remove entries with invalid structure only
  // Don't filter by row existence - let DataGrid manage its own state
  // This prevents "Cannot set properties of undefined" errors while allowing DataGrid to work
  useEffect(() => {
    try {
      const currentRowModesModel = rowModesModelRef.current || {};
      const cleanedRowModesModel: GridRowModesModel = {};
      let hasChanges = false;

      // Only remove entries with invalid structure, not entries for rows that don't exist
      // DataGrid needs to manage rows that might be in transition
      Object.keys(currentRowModesModel).forEach((rowId) => {
        try {
          const modeEntry = currentRowModesModel[rowId];
          
          // Validate mode entry structure only
          if (modeEntry && 
              typeof modeEntry === 'object' && 
              'mode' in modeEntry &&
              (modeEntry.mode === GridRowModes.View || 
               modeEntry.mode === GridRowModes.Edit)) {
            // Entry is valid, keep it
            cleanedRowModesModel[rowId] = modeEntry;
          } else {
            // Invalid structure, remove it
            hasChanges = true;
          }
        } catch (entryError) {
          console.warn(`Error processing row mode entry for ${rowId}:`, entryError);
          hasChanges = true; // Remove invalid entry
        }
      });

      // Only update if there were invalid entries to remove
      if (hasChanges) {
        safeSetRowModesModel(cleanedRowModesModel);
      }
    } catch (error) {
      console.error("Error in rowModesModel cleanup:", error);
      // On error, don't reset - let DataGrid manage its state
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows]);

  // Ensure rowModesModel is always a valid object
  // Keep ref in sync
  useEffect(() => {
    if (!rowModesModel || typeof rowModesModel !== 'object') {
      rowModesModelRef.current = {};
    } else {
      rowModesModelRef.current = rowModesModel;
    }
  }, [rowModesModel]);

  type ExtendedGridColDef = {
    hidden?: boolean;
    export?: boolean;
  } & GridColDef;

  const getGridRenderErrors = useCallback(
    (params: GridRenderEditCellParams, field?: string): boolean | undefined => {
      const errors: any = params.error || validationErrors[params.id]?.errors[field || params.field]?.join(" ");
      return errors ?? false;
    },
    [validationErrors],
  );

  const getGridPreProcessErrors = useCallback(
    (params: GridPreProcessEditCellProps, field?: string): boolean | string | undefined => {
      const errors: any = params.props.error || validationErrors[params.id]?.errors[field || params.props.field]?.join(" ");
      return errors;
    },
    [validationErrors],
  );

  const [defaultColumns, setDefaultColumns] = useState<ExtendedGridColDef[]>([]);
  const [displayColumns, setDisplayColumns] = useState<ExtendedGridColDef[]>([]);

  useEffect(() => {
    const clientKey = getClientConfigKey(appStore.applicationSettings);
    const config = getClientColumnConfig(clientKey);

    if (!config) {
      setConfigErrorMessage(`Failed to load column configuration for client: ${clientKey}`);
      setShowConfigErrorMessage(true);
      return;
    }

    // Validate configuration
    const validation = validateClientConfig(clientKey);
    if (!validation.isValid) {
      setConfigErrorMessage(`Invalid configuration for client ${clientKey}: ${validation.errors.join(", ")}`);
      setShowConfigErrorMessage(true);
      return;
    }

    if (validation.warnings.length > 0) {
      console.warn(`Configuration warnings for client ${clientKey}:`, validation.warnings);
    }

    // Clear any previous configuration errors
    setShowConfigErrorMessage(false);
    setConfigErrorMessage("");

    const baseColumns = config.columns || [];

    const columns = baseColumns.map((col: any) => getColumnByField(col)).filter(Boolean) as ExtendedGridColDef[];

    setDefaultColumns(columns);
    setDisplayColumns(columns); // Initially, display columns same as default columns
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appStore.features, co2MaterialTypeOptions?.data, co2MaterialTypeOptions?.data?.units, co2MaterialTypeOptions?.data?.co2MaterialLibraries, co2MaterialTypeOptions?.data?.co2MaterialCategories]);

  const getI18nText = useCallback(
    (headerNameKey: string, descriptionKey: string) => ({
      headerName: i18n.t(`${headerNameKey}`),
      description: i18n.t(`${descriptionKey}`),
      placeholder: i18n.t(`${headerNameKey}`),
    }),
    [],
  );

  const forceExitAllEditModes = async () => {
    if (!apiRef.current) {
      // If apiRef is not available, just clear the model
      setRowModesModel({});
      rowModesModelRef.current = {};
      return;
    }

    try {
      const model = rowModesModelRef.current || {};
      const ids = Object.keys(model);

      if (ids.length === 0) {
        // Nothing to do, model is already empty
        return;
      }

      // First, ensure all rows that exist are in the model with valid structure
      // This prevents "Cannot set properties of undefined" errors
      const modelWithAllRows: GridRowModesModel = {};
      for (const id of ids) {
        try {
          const row = apiRef.current.getRow(id);
          if (row) {
            // Ensure entry exists with valid structure
            const currentEntry = model[id];
            if (currentEntry && typeof currentEntry === 'object' && 'mode' in currentEntry) {
              modelWithAllRows[id] = currentEntry;
            } else {
              // Initialize with View mode if entry is invalid
              modelWithAllRows[id] = { mode: GridRowModes.View };
            }
          }
        } catch (e) {
          // Skip rows that don't exist
          console.debug(`Row ${id} doesn't exist, skipping`);
        }
      }

      // Update model to ensure all entries exist before stopping
      if (Object.keys(modelWithAllRows).length > 0) {
        setRowModesModel(modelWithAllRows);
        rowModesModelRef.current = modelWithAllRows;
        // Small delay to ensure state is updated and DataGrid processes it
        await new Promise(resolve => setTimeout(resolve, 100));
      }

      // Now stop edit modes for all rows that exist and are in Edit mode
      for (const id of Object.keys(modelWithAllRows)) {
        try {
          const row = apiRef.current.getRow(id);
          if (row) {
            // Ensure row is in model with Edit mode before stopping (if it was in Edit mode)
            const currentEntry = modelWithAllRows[id];
            if (currentEntry.mode === GridRowModes.Edit) {
              // Row was in edit mode, ensure it's still in model before stopping
              const currentModel = rowModesModelRef.current || {};
              if (!currentModel[id] || currentModel[id].mode !== GridRowModes.Edit) {
                const updatedModel = { ...currentModel, [id]: { mode: GridRowModes.Edit } };
                setRowModesModel(updatedModel);
                rowModesModelRef.current = updatedModel;
                await new Promise(resolve => setTimeout(resolve, 10));
              }
              apiRef.current.stopRowEditMode({ id, ignoreModifications: true });
            }
          }
        } catch (e) {
          // Ignore errors - row might not exist or already stopped
          console.debug(`Error stopping edit mode for row ${id}:`, e);
        }
      }

      // Don't clear the model immediately - let DataGrid manage it
      // DataGrid will update the model through onRowModesModelChange
      // Clearing it here causes "Cannot set properties of undefined" errors
      // The model will be cleaned up naturally when DataGrid processes the stop operations
    } catch (error) {
      console.error("Error in forceExitAllEditModes:", error);
      // On error, don't clear immediately - let DataGrid handle it
      // Clearing here can cause race conditions with DataGrid's internal state
    }
  };

  const getColumnByField = useCallback(
    (col: any): ExtendedGridColDef | undefined => {
      const i18nText = getI18nText(col.headerNameKey, col.descriptionKey);
      const baseColumn: Partial<ExtendedGridColDef> = {
        field: col.field,
        ...i18nText,
        flex: col.flex || 1,
        minWidth: col.minWidth,
        editable: col.editable !== undefined ? col.editable && canEdit : canEdit,
        hidden: col.hidden,
        export: col.export !== undefined ? col.export : true,
      };

      // Handle sourceProperty columns first
      if (col.sourceProperty && col.sourceProperty.trim() !== "") {
        return {
          ...baseColumn,
          type: col.type || "string",
          valueGetter: (_value: any, row: any) => {
            try {
              if (row.sourceData != undefined) {
                return col.sourceProperty.split(".").reduce((acc: any, key: string) => acc?.[key], row.sourceData);
              }
              return "";
            } catch {
              return "";
            }
          },
        } as ExtendedGridColDef;
      }

      // Column type factories
      const columnFactories: Record<string, () => ExtendedGridColDef> = {
        // Special columns with custom logic
        rowNum: (): ExtendedGridColDef =>
          ({
            ...baseColumn,
            type: "number",
            renderCell: (params: any) => +params.row.rowId + 1,
          }) as ExtendedGridColDef,

        importStatus: (): ExtendedGridColDef =>
          ({
            ...baseColumn,
            type: "custom",
            align: "center" as const,
            renderCell: (params: any) => (
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", width: "100%", height: "100%" }}>
                {params.row.importStatus === "Error" && <ErrorIcon sx={{ color: theme.palette.error.light }} />}
                {params.row.importStatus === "Warning" && <WarningIcon sx={{ color: theme.palette.warning.light }} />}
                {params.row.importStatus === "Success" && <SuccessIcon sx={{ color: theme.palette.success.light }} />}
              </Box>
            ),
          }) as ExtendedGridColDef,

        // Standard string columns with validation
        name: (): ExtendedGridColDef =>
          ({
            ...baseColumn,
            type: "string",
            preProcessEditCellProps: (params: GridPreProcessEditCellProps) => ({
              ...params.props,
              error: _.isNil(params.props.value),
            }),
            renderEditCell: (params: GridRenderEditCellParams) => <GridEditInputCell {...params} error={getGridRenderErrors(params)} placeholder={i18nText.placeholder} />,
          }) as ExtendedGridColDef,

        // Autocomplete columns
        category: () =>
          createAutocompleteColumn(baseColumn, i18nText, {
            id: "category-select",
            label: "Category",
            options: () => {
              return co2MaterialTypeOptionsRef.current?.co2MaterialCategories || co2MaterialTypeOptions?.data?.co2MaterialCategories || [];
            },
            getValue: (row: any) => {
              const categories = co2MaterialTypeOptionsRef.current?.co2MaterialCategories || co2MaterialTypeOptions?.data?.co2MaterialCategories || [];

              // If co2MaterialCategory is already an object, return it directly
              if (row.co2MaterialCategory && typeof row.co2MaterialCategory === 'object' && row.co2MaterialCategory.co2MaterialCategoryId) {
                return row.co2MaterialCategory;
              }

              // If co2MaterialCategoryId exists as a string/number, find matching category
              if (row.co2MaterialCategoryId) {
                const found = categories.find(
                  (x) => x.co2MaterialCategoryId === row.co2MaterialCategoryId || x.id === row.co2MaterialCategoryId
                );
                if (found) {
                  return found;
                }
              }

              // If category name exists as string, find matching category
              if (typeof row.co2MaterialCategory === 'string' && row.co2MaterialCategory.trim()) {
                const found = categories.find(
                  (x) => x.name === row.co2MaterialCategory || x.name?.toLowerCase() === row.co2MaterialCategory?.toLowerCase()
                );
                if (found) {
                  return found;
                }
              }
              return null;
            },
            errorField: "co2MaterialCategoryId",
            freeSolo: true,
            valueGetter: (_value: any, row: any) => {
              // Handle different data structures
              if (row.co2MaterialCategory) {
                if (typeof row.co2MaterialCategory === 'string') return row.co2MaterialCategory;
                if (typeof row.co2MaterialCategory === 'object') return row.co2MaterialCategory.name || '';
              }
              return '';
            },
          }),

        unit: () =>
          createAutocompleteColumn(baseColumn, i18nText, {
            id: "unit-select",
            label: "Units",
            // Use function to access latest data from component scope via ref
            options: () => {
              // Access latest data from ref to avoid closure issues
              return co2MaterialTypeOptionsRef.current?.units || co2MaterialTypeOptions?.data?.units || [];
            },
            getValue: (row: any) => {
              // Access latest data when getting value from ref
              const units = co2MaterialTypeOptionsRef.current?.units || co2MaterialTypeOptions?.data?.units || [];
              if (typeof row.unit === 'string') {
                return units.find((x) => x.value === row.unit) || null;
              }
              return row.unit || null;
            },
            freeSolo: false,
          }),

        library: () =>
          createAutocompleteColumn(baseColumn, i18nText, {
            id: "library-select",
            label: "Material Library",
            // Use function to access latest data from component scope via ref
            options: () => {
              // Access latest data from ref to avoid closure issues
              return co2MaterialTypeOptionsRef.current?.co2MaterialLibraries || co2MaterialTypeOptions?.data?.co2MaterialLibraries || [];
            },
            getValue: (row: any) => {
              // Access latest data when getting value from ref
              const libraries = co2MaterialTypeOptionsRef.current?.co2MaterialLibraries || co2MaterialTypeOptions?.data?.co2MaterialLibraries || [];
              if (row.library && typeof row.library === 'object') {
                return libraries.find(
                  (x) => x.co2MaterialLibraryId === row.library?.co2MaterialLibraryId
                ) || null;
              }
              if (row.co2MaterialLibrary && typeof row.co2MaterialLibrary === 'object') {
                return libraries.find(
                  (x) => x.co2MaterialLibraryId === row.co2MaterialLibrary?.co2MaterialLibraryId
                ) || null;
              }
              return null;
            },
            errorField: "co2MaterialLibraryId",
            freeSolo: true,
            valueGetter: (_value: any, row: any) => row.co2MaterialLibrary?.name || row.co2MaterialLibraryName || "",
          }),

        densityType: () =>
          createAutocompleteColumn(baseColumn, i18nText, {
            id: "density-indicator-select",
            label: i18n.t("material-grid.columns.auto-complete-labels.density-type"),
            options: densityIndicator,
            getValue: (row: any) => row.densityType || null,
            freeSolo: false,
          }),

        // Date columns
        createdAt: () => createDateColumn(baseColumn),
        modifiedAt: () => createDateColumn(baseColumn),

        // String columns with pattern validation
        masterFormatNumber: (): ExtendedGridColDef =>
          ({
            ...baseColumn,
            type: "string",
            preProcessEditCellProps: (params: GridPreProcessEditCellProps) => {
              const pattern = /^\d\d \d\d \d\d$/;
              const isValid = _.isNil(params.props.value) || pattern.test(params.props.value);
              return { ...params.props, error: !isValid };
            },
            renderEditCell: (params: GridRenderEditCellParams) => <GridEditInputCell {...params} error={getGridRenderErrors(params)} placeholder={i18nText.placeholder} />,
          }) as ExtendedGridColDef,

        // Simple string columns
        co2MaterialTypeId: () => createStringColumn(baseColumn, i18nText, true),

        deleteStatus: (): ExtendedGridColDef => ({ ...baseColumn, type: "string" }) as ExtendedGridColDef,

        // Actions column
        actions: (): ExtendedGridColDef =>
          ({
            ...i18nText,
            field: "actions",
            type: "actions",
            resizable: false,
            width: 100,
            hidden: col.hidden,
            export: col.export ?? false,
            getActions: ({ id, row }: any) => {
              const isInEditMode = rowModesModelRef.current[id]?.mode === GridRowModes.Edit;
              if (isInEditMode) {
                return [
                  <GridActionsCellItem
                    key={`save-${id}`}
                    icon={
                      <Tooltip title={i18n.t("materials-tab.grid.save-button.tooltip")}>
                        <SaveIcon />
                      </Tooltip>
                    }
                    label={i18n.t("materials-tab.grid.save-button.label")}
                    onClick={() => handleSaveClick(id)}
                    sx={{ color: "primary.main" }}
                  />,
                  <GridActionsCellItem
                    key={`cancel-${id}`}
                    icon={
                      <Tooltip title={i18n.t("materials-tab.grid.cancel-button.tooltip")}>
                        <CancelIcon />
                      </Tooltip>
                    }
                    label={i18n.t("materials-tab.grid.cancel-button.label")}
                    onClick={() => handleCancelClick(id)}
                  />,
                ];
              } else if (row.status === "Deleted") {
                return [
                  <GridActionsCellItem
                    key={`restore-${id}`}
                    icon={
                      <Tooltip title={i18n.t("materials-tab.grid.restore-button.tooltip")}>
                        <ResetIcon />
                      </Tooltip>
                    }
                    label={i18n.t("materials-tab.grid.restore-button.label")}
                    onClick={() => handleRestoreClick(id, row)}
                  />,
                ];
              } else {
                return [
                  <GridActionsCellItem
                    key={`edit-${id}`}
                    icon={
                      <Tooltip title={i18n.t("materials-tab.grid.edit-button.tooltip")}>
                        <EditIcon />
                      </Tooltip>
                    }
                    label={i18n.t("materials-tab.grid.edit-button.label")}
                    onClick={() => handleEditClick(id)}
                  />,
                  <GridActionsCellItem
                    key={`delete-${id}`}
                    icon={
                      <Tooltip title={i18n.t("materials-tab.grid.delete-button.tooltip")}>
                        <DeleteIcon />
                      </Tooltip>
                    }
                    label={i18n.t("materials-tab.grid.delete-button.label")}
                    onClick={() => handleDeleteClick(id, row)}
                  />,
                ];
              }
            },
          }) as ExtendedGridColDef,
      };

      // Get the factory function for this column type
      const factory = columnFactories[col.field];

      if (factory) {
        return factory();
      }

      // For any other numeric columns (like b1, b2, c1, c2, etc.)
      if (col.type === "number") {
        return createNumberColumn(baseColumn, i18nText);
      }

      // Default to string column
      return createStringColumn(baseColumn, i18nText);
    },
    [canEdit, co2MaterialTypeOptions?.data, co2MaterialTypeOptions?.data?.units, co2MaterialTypeOptions?.data?.co2MaterialLibraries, co2MaterialTypeOptions?.data?.co2MaterialCategories, densityIndicator, theme, validationErrors, rowModesModelRef, getI18nText],
  );

  // Helper functions for column creation
  const createNumberColumn = useCallback(
    (baseColumn: Partial<ExtendedGridColDef>, i18nText: any): ExtendedGridColDef =>
      ({
        ...baseColumn,
        type: "number",
        preProcessEditCellProps: (params: GridPreProcessEditCellProps) => ({
          ...params.props,
          error: _.lt(params.props.value, 0) || !_.isFinite(params.props.value),
        }),
        renderEditCell: (params: GridRenderEditCellParams) => <GridEditInputCell {...params} error={getGridRenderErrors(params)} placeholder={i18nText.placeholder} />,
        valueFormatter: (value: any) => {
          if (value === undefined || value === null) return "";
          return i18n.t("number", { value, digits: AppSettingType.NumberFormat_EC_Digits });
        },
      }) as ExtendedGridColDef,
    [getGridRenderErrors],
  );

  const createStringColumn = useCallback(
    (baseColumn: Partial<ExtendedGridColDef>, i18nText: any, requiresValidation = false): ExtendedGridColDef =>
      ({
        ...baseColumn,
        type: "string",
        ...(requiresValidation && {
          preProcessEditCellProps: (params: GridPreProcessEditCellProps) => ({
            ...params.props,
            error: _.isNil(params.props.value),
          }),
        }),
        renderEditCell: (params: GridRenderEditCellParams) => <GridEditInputCell {...params} error={getGridRenderErrors(params)} placeholder={i18nText.placeholder} />,
      }) as ExtendedGridColDef,
    [getGridRenderErrors],
  );

  const createAutocompleteColumn = useCallback(
    (
      baseColumn: Partial<ExtendedGridColDef>,
      i18nText: any,
      optionsConfig: {
        id: string;
        label: string;
        options: any[] | (() => any[]);
        getValue: (row: any) => any;
        errorField?: string;
        freeSolo?: boolean;
        valueGetter?: (value: any, row: any) => any;
      },
    ): ExtendedGridColDef => {
      // Store options config in closure but access data dynamically in renderEditCell
      return {
        ...baseColumn,
        type: "string",
        preProcessEditCellProps: (params: GridPreProcessEditCellProps) => ({
          ...params.props,
          error: _.isNil(params.props.value),
        }),
        ...(optionsConfig.valueGetter && { valueGetter: optionsConfig.valueGetter }),
        renderEditCell: (params: GridRenderEditCellParams) => {
            const { id, field, row } = params;
            const onChange = async (_event: any, newValue: any) => {
            await params.api.setEditCellValue({ id, field, value: newValue });
            params.api.stopCellEditMode({ id, field });
          };
          // Get current options dynamically - support both array and function
          // Access co2MaterialTypeOptions from component scope (via closure) to get latest data
          const currentOptions = typeof optionsConfig.options === 'function' 
                ? optionsConfig.options()
            : (optionsConfig.options || []);
          const currentValue = optionsConfig.getValue(row);
          // Use a key that includes data state to force re-render when data loads
          const dataLoaded = co2MaterialTypeOptions?.data ? 'loaded' : 'loading';
          const optionsKey = `${optionsConfig.id}-${id}-${currentOptions.length}-${dataLoaded}`;
          console.log(`DataGridAutocomplete render: ${optionsConfig.id}, options: ${currentOptions.length}, loaded: ${dataLoaded}`);
            return (
              <DataGridAutocomplete
              key={optionsKey}
                id={optionsConfig.id}
                label={optionsConfig.label}
                rows={currentOptions}
                value={currentValue}
                onChange={onChange}
                hasFocus={params.hasFocus}
                error={getGridRenderErrors(params, optionsConfig.errorField)}
                placeholder={i18nText.placeholder}
                freeSolo={optionsConfig.freeSolo !== false}
              />
            );
          },
      } as ExtendedGridColDef;
    },
    [getGridRenderErrors, co2MaterialTypeOptions?.data, co2MaterialTypeOptions?.data?.units, co2MaterialTypeOptions?.data?.co2MaterialLibraries, co2MaterialTypeOptions?.data?.co2MaterialCategories],
  );

  const createDateColumn = useCallback(
    (baseColumn: Partial<ExtendedGridColDef>): ExtendedGridColDef =>
      ({
        ...baseColumn,
        type: "dateTime",
        editable: false,
        renderCell: (params: any) => {
          const date = params.row[baseColumn.field!] ? new Date(params.row[baseColumn.field!]) : new Date();
          return i18n.t("datetime_short", { value: date });
        },
      }) as ExtendedGridColDef,
    [],
  );

  const fieldsToExport = useMemo(() => {
    return displayColumns.filter((col) => col.export !== false).map((col) => col.field);
  }, [displayColumns]);

  // Helper function to generate initial column visibility model
  const getInitialColumnVisibilityModel = () => {
    if (defaultColumns.length === 0) {
      return {
        rowNum: false,
        importStatus: false,
        importAction: false,
        importStatusMessage: false,
      };
    }

    const baseVisibilityModel = defaultColumns.reduce((acc: any, column: any) => {
      if (column.hidden) {
        acc[column.field] = false;
      }
      return acc;
    }, {});

    // Always hide import-related columns by default
    baseVisibilityModel.rowNum = false;
    baseVisibilityModel.importStatus = false;
    baseVisibilityModel.importAction = false;
    baseVisibilityModel.importStatusMessage = false;

    return baseVisibilityModel;
  };

  // Initialize column visibility whenever defaultColumns changes
  useEffect(() => {
    if (defaultColumns.length > 0) {
      const columnVisibilityModel = getInitialColumnVisibilityModel();
      setColumnVisibilityModel(columnVisibilityModel);
      setDisplayColumns(defaultColumns); // Ensure display columns are in sync
      console.log("?? Column visibility model initialized:", columnVisibilityModel);
    }
  }, [defaultColumns]);

  // Ensure actions column is visible for default clients (not during import mode)
  useEffect(() => {
    if (defaultColumns.length > 0 && editMode !== EditMode.Import) {
      const actions = defaultColumns.find((col) => col.field === "actions")?.hidden === false ? true : false;
      setColumnVisibilityModel((prev: any) => ({
        ...prev,
        actions,
      }));
    }
  }, [defaultColumns, editMode, appStore.features]);

  // Cleanup effect to reset import mode when component unmounts
  useEffect(() => {
    return () => {
      // Reset any import mode state when component unmounts
      setEditMode(EditMode.ReadOnly);
      setShowSuccessMessage(false);
      setShowErrorMessage(false);
      setShowWarningMessage(false);
      setShowConfigErrorMessage(false);
    };
  }, []);

  const handleAddRow = () => {
    // Check if there's already a new row being added
    const hasNewRow = rows.some((r: any) => r.isNew === true);
    if (hasNewRow) {
      console.warn("A new row is already being added. Please save or cancel it first.");
      return;
    }

    // Generate a unique temporary ID (use negative number to avoid conflicts with real IDs)
    // Use a large negative number that's unlikely to conflict
    const tempId = -(Date.now() % 1000000000);
    
    // TODO: create instance of Co2MaterialType for HTTP Post/Create
    const co2MaterialTypeItem: any = {
      /* TODO */
      id: tempId,
      co2MaterialTypeId: tempId, // Use the same tempId so getRowId works correctly
      name: "",
      description: "",
      // TODO: these should be nullable now
      a1A2A3: null,
      a1A2A3_ECF: null,
      a4: null,
      a4_ECF: null,
      a5: null,
      a5_ECF: null,
      // TODO: add new fields
      unit: "m3",
      unitQuantity: 1,
      co2MaterialLibrary: null,
      massPerUnit: 0,
      density: 0,
      source: "",
      co2MaterialCategory: {
        name: null,
      },
    };
    
    // Add the new row (it will be in View mode by default)
    setRows((oldRows: any) => [{ ...co2MaterialTypeItem, isNew: true }, ...oldRows]);
    setEditMode(EditMode.Add);
    
    // Don't set row mode to Edit immediately - let the row be in View mode first
    // We'll set it to Edit after the DataGrid has processed the new row
    
    // Start edit mode after ensuring the row is in View mode and DataGrid has processed it
    const startEditMode = async () => {
      try {
        // First, verify the row exists and is in View mode
        const row = apiRef.current.getRow(tempId);
        if (!row) {
          console.warn("Row not found yet, retrying...");
          setTimeout(startEditMode, 100);
          return;
        }
        
        const currentMode = apiRef.current.getRowMode(tempId);
        console.log("Current row mode:", currentMode);
        
        // If row is not in View mode, wait a bit more
        if (currentMode !== GridRowModes.View && currentMode !== GridRowModes.Edit) {
          console.warn("Row not in View mode yet, retrying...");
          setTimeout(startEditMode, 100);
          return;
        }
        
        // Ensure row is in rowModesModel with Edit mode before starting edit mode
        // This prevents "Cannot set properties of undefined" errors
        const currentRowModesModel = rowModesModelRef.current || {};
        const newRowModesModel = { 
          ...currentRowModesModel, 
          [tempId]: { mode: GridRowModes.Edit } 
        };
        setRowModesModel(newRowModesModel);
        rowModesModelRef.current = newRowModesModel;
        
        // Small delay to ensure state is updated before starting edit mode
        await new Promise(resolve => setTimeout(resolve, 50));
        
        // Now start edit mode
        apiRef.current.startRowEditMode({ id: tempId });
        console.log("Row edit mode started successfully");
        
        setTimeout(() => {
          setCoordinates({ rowIndex: 0, colIndex: 0 });
        }, 100);
      } catch (error) {
        console.error("Error starting row edit mode:", error);
        // Retry once after a longer delay
        setTimeout(async () => {
          try {
            if (apiRef.current) {
              const row = apiRef.current.getRow(tempId);
              if (row) {
                const currentRowModesModel = rowModesModelRef.current || {};
                const newRowModesModel = { 
                  ...currentRowModesModel, 
                  [tempId]: { mode: GridRowModes.Edit } 
                };
                setRowModesModel(newRowModesModel);
                rowModesModelRef.current = newRowModesModel;
                // Small delay before starting edit mode
                await new Promise(resolve => setTimeout(resolve, 50));
                apiRef.current.startRowEditMode({ id: tempId });
              }
            }
          } catch (retryError) {
            console.error("Error retrying row edit mode:", retryError);
          }
        }, 500);
      }
    };
    
    // Start the process after a delay to let the DataGrid process the new row
    setTimeout(startEditMode, 100);
  };

  const handleShowArchived = (showArchived: boolean) => {
    setShowArchived(showArchived);
  };

  const handleEditClick = async (id: GridRowId) => {
    console.log("Edit clicked: ", id);
    try {
      // Ensure row is in rowModesModel with Edit mode before starting edit mode
      // This prevents "Cannot set properties of undefined" errors
      const currentRowModesModel = rowModesModelRef.current || {};
      const newRowModesModel = { 
        ...currentRowModesModel, 
        [id]: { mode: GridRowModes.Edit } 
      };
      setRowModesModel(newRowModesModel);
      rowModesModelRef.current = newRowModesModel;
      
      // Small delay to ensure state is updated before starting edit mode
      await new Promise(resolve => setTimeout(resolve, 50));
      
      // Start row edit mode
      apiRef.current.startRowEditMode({ id });
      setEditMode(EditMode.Edit);
    } catch (error) {
      console.error("Error in handleEditClick:", error);
    }
  };

  const handleDeleteClick = async (id: GridRowId, _row: any) => {
    console.log("Delete clicked: ", id);
    try {
      // Delete the material
      await deleteCo2MaterialType.mutateAsync(id as any);
      
      forceExitAllEditModes();
      // Refetch data to update the grid
      await co2MaterialTypes.refetch();
      
      // Safely update row mode if it was in edit mode
      const currentRowModesModel = rowModesModelRef.current || {};
      if (currentRowModesModel[id]) {
        const newRowModesModel = { ...currentRowModesModel };
        delete newRowModesModel[id];
        safeSetRowModesModel(newRowModesModel);
      }
    } catch (error) {
      console.error("Error in handleDeleteClick:", error);
      throw error;
    }
  };

  const handleRestoreClick = async (_id: GridRowId, _row: any) => {
    // TODO: handle restore material
  };

  const handleSaveClick = async (id: GridRowId) => {
    console.log("Save clicked: ", id);
    try {
      // Check if apiRef is available
      if (!apiRef.current) {
        console.error("apiRef.current is null, cannot save");
        return;
      }

      // Get the base row from DataGrid
      const baseRow = apiRef.current.getRow(id);
      console.log("Base row from API:", baseRow);
      
      if (!baseRow) {
        console.error("Could not get row from DataGrid");
        return;
      }

      // Get the edited cell values from DataGrid's edit state
      const editRowsState = apiRef.current.state?.editRows || {};
      const rowEditState = editRowsState[id];
      console.log("Row edit state:", rowEditState);
      
      // Merge edited values with base row to get the complete edited row
      let editedRow = { ...baseRow };
      if (rowEditState) {
        // Apply all edited cell values to the row
        Object.keys(rowEditState).forEach((field) => {
          const cellEditState = rowEditState[field];
          if (cellEditState && cellEditState.value !== undefined) {
            editedRow[field] = cellEditState.value;
          }
        });
      }
      
      console.log("Edited row (merged):", editedRow);
      
      // Find the original row from our rows state for comparison
      // Use co2MaterialTypeId since that's what getRowId returns
      // The id parameter is the co2MaterialTypeId (from getRowId)
      let originalRow = rows.find((r: any) => {
        // Match by co2MaterialTypeId (which is what getRowId returns)
        return r.co2MaterialTypeId === id;
      });
      
      // If not found, try matching by baseRow's co2MaterialTypeId
      if (!originalRow && baseRow?.co2MaterialTypeId) {
        originalRow = rows.find((r: any) => {
          return r.co2MaterialTypeId === baseRow.co2MaterialTypeId;
        });
      }
      
      // If still not found, try using the baseRow itself as the original
      // This can happen for new rows that were just added
      if (!originalRow) {
        console.warn("Could not find original row in rows array, using baseRow as original");
        originalRow = baseRow;
      }
      
      console.log("Original row:", originalRow);
      console.log("Available rows count:", rows.length);
      console.log("Looking for id:", id);
      console.log("baseRow.co2MaterialTypeId:", baseRow?.co2MaterialTypeId);
      
      // Directly call processRowUpdate with the edited and original rows
      // This ensures the API call happens regardless of stopRowEditMode behavior
      console.log("Calling processRowUpdate directly...");
      try {
        await processRowUpdate(editedRow, originalRow);
        console.log("processRowUpdate completed successfully");
      } catch (processError) {
        console.error("Error in processRowUpdate:", processError);
        throw processError; // Re-throw to be caught by outer catch
      }
      
      // After successful save, stop row edit mode
      // CRITICAL: Ensure the row is in rowModesModel BEFORE calling stopRowEditMode
      // DataGrid's internal code expects prevRowModesModel.current[id] to exist
      if (apiRef.current) {
        const rowStillExists = apiRef.current.getRow(id);
        if (rowStillExists) {
          // Ensure the row is in the model with Edit mode before stopping
          // This prevents "Cannot set properties of undefined" errors
          const currentRowModesModel = rowModesModelRef.current || {};
          if (!currentRowModesModel[id] || currentRowModesModel[id].mode !== GridRowModes.Edit) {
            // Row not in model or not in Edit mode - add it first
            const modelWithRow = { 
              ...currentRowModesModel, 
              [id]: { mode: GridRowModes.Edit } 
            };
            setRowModesModel(modelWithRow);
            rowModesModelRef.current = modelWithRow;
            // Small delay to ensure state is updated
            await new Promise(resolve => setTimeout(resolve, 50));
          }
          
          // Now stop row edit mode - the row is guaranteed to be in the model
          // try {
          //   await apiRef.current.stopRowEditMode({ id });
          // } catch (stopError) {
          //   console.warn("Error stopping row edit mode (non-critical, data already saved):", stopError);
          // }
        } else {
          // Row doesn't exist anymore (might have been removed during refetch)
          // Clean up the mode entry AFTER ensuring we don't call stopRowEditMode
          const currentRowModesModel = rowModesModelRef.current || {};
          const newRowModesModel = { ...currentRowModesModel };
          delete newRowModesModel[id];
          setRowModesModel(newRowModesModel);
          rowModesModelRef.current = newRowModesModel;
        }
      }
      
      setEditMode(EditMode.ReadOnly);
    } catch (error) {
      console.error("Error in handleSaveClick:", error);
      console.error("Error details:", {
        message: error instanceof Error ? error.message : String(error),
        stack: error instanceof Error ? error.stack : undefined,
      });
      
      // On error, still try to exit edit mode safely
      // CRITICAL: Ensure the row is in rowModesModel BEFORE calling stopRowEditMode
      // DataGrid's internal code expects prevRowModesModel.current[id] to exist
      if (apiRef.current) {
        try {
          const rowStillExists = apiRef.current.getRow(id);
          if (rowStillExists) {
            // Ensure the row is in the model with Edit mode before stopping
            const currentRowModesModel = rowModesModelRef.current || {};
            if (!currentRowModesModel[id] || currentRowModesModel[id].mode !== GridRowModes.Edit) {
              // Row not in model or not in Edit mode - add it first
              const modelWithRow = { 
                ...currentRowModesModel, 
                [id]: { mode: GridRowModes.Edit } 
              };
              setRowModesModel(modelWithRow);
              rowModesModelRef.current = modelWithRow;
              // Small delay to ensure state is updated
              await new Promise(resolve => setTimeout(resolve, 50));
            }
            
            // Now stop row edit mode - the row is guaranteed to be in the model
            // try {
            //   await apiRef.current.stopRowEditMode({ id });
            // } catch (stopError) {
            //   console.warn("Error stopping row edit mode during cleanup:", stopError);
            // }
          } else {
            // Row doesn't exist, clean up the mode entry
            const currentRowModesModel = rowModesModelRef.current || {};
            const newRowModesModel = { ...currentRowModesModel };
            delete newRowModesModel[id];
            setRowModesModel(newRowModesModel);
            rowModesModelRef.current = newRowModesModel;
          }
        } catch (getRowError) {
          console.warn("Error getting row during cleanup:", getRowError);
        }
      }
      
      setEditMode(EditMode.ReadOnly);
      // Re-throw the error so it can be handled by the error handler
      throw error;
    }
  };

  const handleCancelClick = (id: GridRowId) => {
    console.log("Cancel clicked: ", id);
    try {
      // CRITICAL: Ensure the row is in rowModesModel BEFORE calling stopRowEditMode
      // DataGrid's internal code expects prevRowModesModel.current[id] to exist
      const currentRowModesModel = rowModesModelRef.current || {};
      
      // Ensure row is in model with Edit mode before stopping
      if (!currentRowModesModel[id] || currentRowModesModel[id].mode !== GridRowModes.Edit) {
        const modelWithRow = { 
          ...currentRowModesModel, 
          [id]: { mode: GridRowModes.Edit } 
        };
        setRowModesModel(modelWithRow);
        rowModesModelRef.current = modelWithRow;
      }

      // Stop row edit mode (only if apiRef is available)
      // if (apiRef.current) {
      //   try {
      //     apiRef.current.stopRowEditMode({ id, ignoreModifications: true });
      //   } catch (stopError) {
      //     console.warn("Error stopping row edit mode (non-critical):", stopError);
      //   }
      // }

      // If it's a new row (isNew: true), remove it from the grid
      const row = rows.find((r: any) => {
        const rowId = r.co2MaterialTypeId; // Use co2MaterialTypeId since that's what getRowId returns
        return rowId === id;
      });
      
      if (row && row.isNew === true) {
        const newRows = rows.filter((r: any) => {
          // Remove the row that matches the cancelled ID
          return r.co2MaterialTypeId !== id;
        });
        setRows(newRows);
      }

      setEditMode(EditMode.ReadOnly);
    } catch (error) {
      console.error("Error in handleCancelClick:", error);
    }
  };

  const handleRowModesModelChange = (newRowModesModel: GridRowModesModel) => {
    try {
      // Ensure newRowModesModel is always an object
      if (!newRowModesModel || typeof newRowModesModel !== 'object') {
        setRowModesModel({});
        rowModesModelRef.current = {};
        return;
      }

      // CRITICAL: Fix invalid entries and ensure all entries have valid structure
      // DataGrid's internal code expects prevRowModesModel.current[id] to exist and have a 'mode' property
      // We must ensure all entries are properly initialized to prevent "Cannot set properties of undefined" errors
      let needsFix = false;
      const fixedModel: GridRowModesModel = {};
      
      for (const rowId in newRowModesModel) {
        const modeEntry = newRowModesModel[rowId];
        
        // Validate and fix entry structure
        if (!modeEntry || typeof modeEntry !== 'object' || !('mode' in modeEntry)) {
          // Invalid entry - initialize with View mode
          fixedModel[rowId] = { mode: GridRowModes.View };
          needsFix = true;
        } else if (modeEntry.mode !== GridRowModes.View && modeEntry.mode !== GridRowModes.Edit) {
          // Invalid mode value - default to View
          fixedModel[rowId] = { mode: GridRowModes.View };
          needsFix = true;
        } else {
          // Valid entry - keep it as-is
          fixedModel[rowId] = modeEntry;
        }
      }
      
      // Use fixed model if there were invalid entries, otherwise use the original
      // This preserves DataGrid's object reference when possible, but ensures all entries are valid
      const modelToSet = needsFix ? fixedModel : newRowModesModel;
      setRowModesModel(modelToSet);
      rowModesModelRef.current = modelToSet;
    } catch (error) {
      console.error("Error in handleRowModesModelChange:", error);
      // On error, use empty model
      setRowModesModel({});
      rowModesModelRef.current = {};
    }
  };

  const handleCellEditStart: GridEventListener<"cellEditStart"> = (params, _event) => {
    console.log("handleCellEditStart: ", params);
    setEditMode(EditMode.Edit);
  };

  const handleCellEditStop: GridEventListener<"cellEditStop"> = (params, _event) => {
    console.log("handleCellEditStop: ", params);
    setEditMode(EditMode.ReadOnly);
  };

  const handleRowEditStart: GridEventListener<"rowEditStart"> = (params, _event) => {
    console.log("handleRowEditStart: ", params);
    setEditMode(EditMode.Edit);
  };

  const handleRowEditStop: GridEventListener<"rowEditStop"> = (params, event) => {
    console.log("handleRowEditStop: ", params);

    if (params.reason === GridRowEditStopReasons.rowFocusOut) {
      event.defaultMuiPrevented = true;
    }

    const pendingChanges = rows.some((r: any) => r.isNew === true);
    setEditMode(pendingChanges ? EditMode.Add : EditMode.ReadOnly); // ??
  };

  const getCo2MaterialCategoryId = (row: GridRowModel): string | undefined => {
    if (typeof row.category === "object") {
      return row.category?.co2MaterialCategoryId;
    }
    if (typeof row.category === "string") {
      if (row.category === row.co2MaterialCategory?.name) {
        return row.co2MaterialCategory?.co2MaterialCategoryId;
      }
    }
    return undefined;
  };

  const getCo2MaterialCategoryName = (row: GridRowModel): string | undefined => {
    // User has imported a custom library name from excel
    if (typeof row.co2MaterialCategory === "string") {
      return row.co2MaterialCategory;
    }
    // User has entered a custom library name in the grid editor and is not one of the existing libraries
    if (typeof row.category === "string") {
      if (row.category === row.co2MaterialCategory?.name) {
        return undefined;
      }
      return row.category;
    }
    return undefined;
  };

  const getCo2MaterialLibraryId = (row: GridRowModel): string | undefined => {
    if (typeof row.library === "object") {
      return row.library?.co2MaterialLibraryId;
    }
    if (typeof row.library === "string") {
      if (row.library === row.co2MaterialLibrary?.name) {
        return row.co2MaterialLibrary?.co2MaterialLibraryId;
      }
    }
    return undefined;
  };

  const getCo2MaterialLibraryName = (row: GridRowModel): string | undefined => {
    // User has imported a custom library name from excel
    if (typeof row.co2MaterialLibrary === "string") {
      return row.co2MaterialLibrary;
    }
    // User has entered a custom library name in the grid editor and is not one of the existing libraries
    if (typeof row.library === "string") {
      if (row.library === row.co2MaterialLibrary?.name) {
        return undefined;
      }
      return row.library;
    }
    return undefined;
  };

  const processRowUpdate = async (newRow: GridRowModel, oldRow: GridRowModel) => {
    console.log("processRowUpdate called with:", { oldRow, newRow });

    // TODO: here we can validate the row and return null if it is not valid

    if (newRow.ignoreModifications) {
      console.log("Row has ignoreModifications flag, skipping save");
      return newRow;
    }
    
    // Check if this is a new row (has isNew flag or negative co2MaterialTypeId)
    const isNewRow = newRow.isNew === true || 
                     (typeof newRow.co2MaterialTypeId === 'number' && newRow.co2MaterialTypeId < 0) ||
                     newRow.co2MaterialTypeId === -1;
    
    console.log("Is new row:", isNewRow);
    console.log("Creating UpsertCo2MaterialTypeRequest...");
    
    const applySaveSuccessCleanup = (currentRows: any[], exitEditModeForRowId?: GridRowId) => {
      const filteredRows = currentRows.filter((r: any) => r.isNew !== true);
      const initialRowModesModel: GridRowModesModel = {};
      filteredRows.forEach((row: any) => {
        const rowId = row.co2MaterialTypeId;
        if (rowId !== undefined && rowId !== null) {
          initialRowModesModel[rowId] = { mode: GridRowModes.View };
        }
      });
      if (isNewRow && newRow.co2MaterialTypeId !== undefined && newRow.co2MaterialTypeId !== null) {
        initialRowModesModel[newRow.co2MaterialTypeId] = { mode: GridRowModes.View };
      }
      setRowModesModel(initialRowModesModel);
      rowModesModelRef.current = initialRowModesModel;
      setRows(filteredRows);
      if (exitEditModeForRowId !== undefined && apiRef.current) {
        setTimeout(() => {
          try {
            apiRef.current?.stopRowEditMode({ id: exitEditModeForRowId, ignoreModifications: true });
          } catch {            
          }
        }, 0);
      }
      co2MaterialTypes.refetch().catch((err) => {
        console.error("Refetch after save:", err);
      });
    };

    try {
      const createCo2MaterialTypeRequest = new UpsertCo2MaterialTypeRequest({
        co2MaterialTypeId: isNewRow ? undefined : newRow.co2MaterialTypeId,
        name: newRow.name,
        description: newRow.description,
        a1A2A3: +newRow.a1A2A3,
        a4: +newRow.a4,
        a5: +newRow.a5,
        b1: +newRow.b1,
        b2: +newRow.b2,
        b3: +newRow.b3,
        b4: +newRow.b4,
        b5: +newRow.b5,
        b6: +newRow.b6,
        b7: +newRow.b7,
        c1: +newRow.c1,
        c2: +newRow.c2,
        c3: +newRow.c3,
        c4: +newRow.c4,
        d: +newRow.d,
        biogenic: +newRow.biogenic,
        wastage: +newRow.wastage,
        unit: newRow.unit?.value ?? newRow.unit,
        co2MaterialLibraryId: getCo2MaterialLibraryId(newRow),
        co2MaterialLibraryName: getCo2MaterialLibraryName(newRow),
        unitQuantity: +newRow.unitQuantity,
        massPerUnit: +newRow.massPerUnit,
        density: +newRow.density,
        source: newRow.source,
        densityType: newRow.densityType?.value,
        co2MaterialCategoryId: getCo2MaterialCategoryId(newRow),
        co2MaterialCategoryName: getCo2MaterialCategoryName(newRow),
      });

      console.log("Request object:", createCo2MaterialTypeRequest);
      console.log("Calling upsertCo2MaterialType.mutateAsync...");

      let response: any;
      try {
        response = await upsertCo2MaterialType.mutateAsync([createCo2MaterialTypeRequest] as any);
      } catch (mutateError) {
        const msg = mutateError instanceof Error ? (mutateError as Error).message : String(mutateError);
        const isDataGridModeError = msg.includes("Cannot set properties of undefined") && msg.includes("'mode'");
        if (isDataGridModeError) {
          console.warn("DataGrid mode sync error after save (data was saved); applying cleanup:", msg);
          applySaveSuccessCleanup(rowsRef.current || [], isNewRow ? undefined : newRow.co2MaterialTypeId);
          return newRow;
        }
        throw mutateError;
      }

      console.log("API response received:", response);

      if (response.failureItems && response.failureItems.length > 0) {
        console.error("API returned validation errors:", response.failureItems);
        const tempId = isNewRow ? newRow.co2MaterialTypeId : newRow.co2MaterialTypeId;
        const errors = { [tempId]: { errors: response.failureItems[0].errors } };
        setValidationErrors(errors);
        throw new Error("Validation error");
      }

      console.log("Save successful, cleaning up...");
      const updatedRowId = isNewRow ? undefined : (newRow.co2MaterialTypeId as GridRowId);
      applySaveSuccessCleanup(rowsRef.current || [], updatedRowId);
      return newRow;
    } catch (error) {
      console.error("Error in processRowUpdate:", error);
      console.error("Error details:", {
        message: error instanceof Error ? error.message : String(error),
        stack: error instanceof Error ? error.stack : undefined,
      });
      const msg = error instanceof Error ? (error as Error).message : String(error);
      const isDataGridModeError = msg.includes("Cannot set properties of undefined") && msg.includes("'mode'");
      if (isDataGridModeError) {
        console.warn("DataGrid mode sync error (data was saved); applying cleanup:", msg);
        applySaveSuccessCleanup(rowsRef.current || [], isNewRow ? undefined : (newRow.co2MaterialTypeId as GridRowId));
        return newRow;
      }
      throw error;
    }
  };

  const handleSearchTextChange = (searchText: string | undefined) => {
    console.log("handleSearchChange: ", searchText);
    setSearchText(searchText || "");
  };

  const handleMaterialLibrarySelectChange = (newSelections: string[]) => {
    console.log("handleMaterialLibrarySelectChange: ", newSelections);
    setMaterialLibraryIds(newSelections);
  };

  const handleFileImport = async (files: File[]) => {
    console.log("handleFileImport: ", files);

    const data = await files[0].arrayBuffer();
    const blob = new Blob([new Uint8Array(data)], { type: files[0].type });
    setImportData({ data: blob, fileName: files[0].name });
    setEditMode(EditMode.Import);
    await uploadTemplate.mutateAsync({
      templateType: TemplateType.Co2MaterialTypes,
      commitChanges: false,
      fileParameter: { data: blob, fileName: files[0].name },
      isDefault: setMaterialsAsDefault(appStore.applicationSettings),
    } as any);
  };

  const handleFileImportCommit = async () => {
    console.log("handleFileImportCommit");
    await uploadTemplate.mutateAsync({
      templateType: TemplateType.Co2MaterialTypes,
      commitChanges: true,
      commitAfterWarning: false,
      fileParameter: importData,
      isDefault: setMaterialsAsDefault(appStore.applicationSettings),
    } as any);

    forceExitAllEditModes();
    // Refetch the data to show the updated database state
    await co2MaterialTypes.refetch();
    await co2MaterialTypeOptions.refetch();

    await endImportMode();
  };

  const handleFileImportCommitPostWarnig = async () => {
    console.log("handleFileImportCommitPostWarnig");
    await uploadTemplate.mutateAsync({
      templateType: TemplateType.Co2MaterialTypes,
      commitChanges: false,
      commitAfterWarning: true,
      fileParameter: importData,
      isDefault: setMaterialsAsDefault(appStore.applicationSettings),
    } as any);

    forceExitAllEditModes();
    // Refetch the data to show the updated database state
    await co2MaterialTypes.refetch();
    await co2MaterialTypeOptions.refetch();

    await endImportMode();
  };

  const handleAbortFileImport = async () => {
    await endImportMode();
  };

  const handleDismissConfigError = () => {
    setShowConfigErrorMessage(false);
    setConfigErrorMessage("");
  };

  const exceljsPreProcess = async ({ workbook, worksheet }: GridExceljsProcessInput) => {
    console.log("exceljsPreProcess", workbook.description, worksheet.name);
  };
  const exceljsPostProcess = async ({ worksheet }: GridExceljsProcessInput) => {
    console.log("exceljsPostProcess", worksheet.name);
  };

  console.log("containerHeight", containerHeight);
  console.log("alertHeight", alertHeight);
  console.log("girdHeight", gridHeight);

  console.log("displayColumns", displayColumns);

  return (
    <Box ref={containerRef} sx={{ display: "flex", flexDirection: "column", width: "100%", height: "100%" }}>
      {showSuccessMessage && (
        <Box ref={alertRef} my={1} sx={{ display: "flex", flexGrow: 0, flexShrink: 1, width: "100%" }}>
          <Alert
            severity="success"
            sx={{
              width: "100%",
              border: 1,
              borderColor: theme.palette.success.main,
              "& .MuiPaper-root": { flexGrow: 1 },
              "& .MuiAlert-message": { flexGrow: 1, overflow: "hidden" },
            }}
          >
            <AlertTitle>{i18n.t("material-grid.validate-success-title")}</AlertTitle>
            <Grid container spacing={2} sx={{ alignItems: "center" }}>
              <Grid size="grow">{i18n.t("materials-grid.validate-success-message")}</Grid>
              <Grid size="auto">
                <Grid container spacing={2}>
                  <Grid size="auto">
                    <Button variant="outlined" color="primary" onClick={handleAbortFileImport}>
                      {i18n.t("materials-grid.abort-changes")}
                    </Button>
                  </Grid>
                  <Grid size="auto">
                    <Button variant="contained" color="primary" onClick={handleFileImportCommit} loading={uploadTemplate.isPending} disabled={uploadTemplate.isPending}>
                      {i18n.t("materials-grid.commit-changes")}
                    </Button>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Alert>
        </Box>
      )}
      {showErrorMessage && (
        <Box ref={alertRef} my={1} sx={{ display: "flex", flexGrow: 0, flexShrink: 1, width: "100%" }}>
          <Alert
            severity="error"
            sx={{
              width: "100%",
              border: 1,
              borderColor: theme.palette.error.main,
              "& .MuiPaper-root": { flexGrow: 1 },
              "& .MuiAlert-message": { flexGrow: 1, overflow: "hidden" },
            }}
          >
            <AlertTitle>{i18n.t("material-grid.error-title")}</AlertTitle>
            <Grid container spacing={2} sx={{ alignItems: "center" }}>
              <Grid size="grow">{i18n.t("materials-grid.error-message")}</Grid>
              <Grid size="auto">
                <Grid container spacing={2}>
                  <Grid size="auto">
                    <Button variant="contained" color="primary" onClick={handleAbortFileImport}>
                      {i18n.t("materials-grid.abort-changes")}
                    </Button>
                  </Grid>
                  <Grid size="auto">
                    <Button variant="contained" color="primary" disabled>
                      {i18n.t("materials-grid.commit-changes")}
                    </Button>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Alert>
        </Box>
      )}

      {showWarningMessage && (
        <Box ref={alertRef} my={1} sx={{ display: "flex", flexGrow: 0, flexShrink: 1, width: "100%" }}>
          <Alert
            severity="warning"
            sx={{
              width: "100%",
              border: 1,
              borderColor: theme.palette.error.main,
              "& .MuiPaper-root": { flexGrow: 1 },
              "& .MuiAlert-message": { flexGrow: 1, overflow: "hidden" },
            }}
          >
            <AlertTitle>{i18n.t("material-grid.warning-title")}</AlertTitle>
            <Grid container spacing={2} sx={{ alignItems: "center" }}>
              <Grid size="grow">{i18n.t("materials-grid.warning-message")}</Grid>
              {/* <Grid size="auto">
                <Grid container spacing={2}>
                  <Grid size="auto">
                    <Button variant="contained" color="primary" onClick={handleAbortFileImport}>
                      {i18n.t("materials-grid.abort-changes")}
                    </Button>
                  </Grid>
                  <Grid size="auto">
                    <Button variant="contained" color="primary" disabled>
                      {i18n.t("materials-grid.commit-changes")}
                    </Button>
                  </Grid>
                </Grid>
              </Grid> */}
              <Grid size="auto">
                <Grid container spacing={2}>
                  <Grid size="auto">
                    <Button variant="outlined" color="primary" onClick={handleAbortFileImport}>
                      {i18n.t("materials-grid.abort-changes")}
                    </Button>
                  </Grid>
                  <Grid size="auto">
                    <Button variant="contained" color="primary" onClick={handleFileImportCommitPostWarnig} loading={uploadTemplate.isPending} disabled={uploadTemplate.isPending}>
                      {i18n.t("materials-grid.commit-changes")}
                    </Button>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
            <Grid container spacing={2} sx={{ alignItems: "center" }}>
              <Grid size="auto">
                <ul>{validationFailure?.map((d, index) => <li key={`${index}`}>{i18n.t(`materials-grid.${d.propertyName}.${d.errorCode}`)}</li>)}</ul>
              </Grid>

              {/* <>
      
            </> */}
              {/* <Grid size="auto">
                <Grid container spacing={2}>
                  <Grid size="auto">
                    <Button variant="contained" color="primary" onClick={handleAbortFileImport}>
                      {i18n.t("materials-grid.abort-changes")}
                    </Button>
                  </Grid>
                  <Grid size="auto">
                    <Button variant="contained" color="primary" disabled>
                      {i18n.t("materials-grid.commit-changes")}
                    </Button>
                  </Grid>
                </Grid>
              </Grid> */}
            </Grid>
          </Alert>
        </Box>
      )}

      {showConfigErrorMessage && (
        <Box ref={alertRef} my={1} sx={{ display: "flex", flexGrow: 0, flexShrink: 1, width: "100%" }}>
          <Alert
            severity="error"
            sx={{
              width: "100%",
              border: 1,
              borderColor: theme.palette.error.main,
              "& .MuiPaper-root": { flexGrow: 1 },
              "& .MuiAlert-message": { flexGrow: 1, overflow: "hidden" },
            }}
          >
            <AlertTitle>Configuration Error</AlertTitle>
            <Grid container spacing={2} sx={{ alignItems: "center" }}>
              <Grid size="grow">{configErrorMessage}</Grid>
              <Grid size="auto">
                <Button variant="outlined" color="primary" onClick={handleDismissConfigError}>
                  Dismiss
                </Button>
              </Grid>
            </Grid>
          </Alert>
        </Box>
      )}

      <Box sx={{ display: "flex", flexGrow: 1, flexShrink: 0, width: "100%" }}>
        <ErrorBoundary
          onReset={() => {}}
          fallbackRender={({ error, resetErrorBoundary }) => {
            const msg = error?.message ?? String(error);
            const isDataGridModeError = msg.includes("Cannot set properties of undefined") && msg.includes("mode");
            if (isDataGridModeError) {
              resetErrorBoundary();
              return null;
            }
            return (
              <Alert severity="error" sx={{ width: "100%" }}>
                <AlertTitle>Error</AlertTitle>
                {msg}
                <Button size="small" onClick={resetErrorBoundary} sx={{ mt: 1 }}>
                  Try again
                </Button>
              </Alert>
            );
          }}
        >
        <DataGrid
          apiRef={apiRef}
          loading={co2MaterialTypes.isFetching || uploadTemplate.isPending}
          columns={displayColumns}
          columnVisibilityModel={columnVisibilityModel}
          onColumnVisibilityModelChange={(columnVisibilityModel: any) => {
            console.log("onColumnVisibilityModelChange", columnVisibilityModel);
            setColumnVisibilityModel(columnVisibilityModel);
          }}
          rows={rows}
          getRowId={(row: any) => row.co2MaterialTypeId}
          hideFooter={false}
          editMode="row"
          //checkboxSelection
          //disableRowSelectionOnClick
          //rowSelectionModel={rowSelectionModel}
          //onRowSelectionModelChange={(ids: GridRowSelectionModel) => {
          //  console.log("onRowSelectionModelChange", ids);
          //  setRowSelectionModel(ids);
          //}}
          rowModesModel={rowModesModel}
          onRowModesModelChange={(newModel) => {
            try {
              handleRowModesModelChange(newModel);
            } catch (error) {
              console.error("Error in onRowModesModelChange:", error);
              // Prevent the error from propagating by clearing the model
              safeSetRowModesModel({});
            }
          }}
          onCellEditStart={handleCellEditStart}
          onCellEditStop={handleCellEditStop}
          onRowEditStart={handleRowEditStart}
          onRowEditStop={handleRowEditStop}
          processRowUpdate={processRowUpdate}
          onProcessRowUpdateError={(error) => console.error("onProcessRowUpdateError: ", error)}
          sortModel={sortModel}
          onSortModelChange={(newSortModel) => setSortModel(newSortModel)}
          slots={{
            toolbar: EditToolbar,
          }}
          slotProps={{
            toolbar: {
              editMode,
              showArchived,
              searchText,
              onAddRow: handleAddRow,
              onShowArchived: handleShowArchived,
              onSearchTextChange: handleSearchTextChange,
              onMaterialLibrarySelectChange: handleMaterialLibrarySelectChange,
              onFileImport: handleFileImport,
              excelOptions: {
                fields: fieldsToExport,
                exceljsPreProcess,
                exceljsPostProcess,
              },
            },
          }}
          initialState={{
            density: "compact",
            columns: {
              columnVisibilityModel: getInitialColumnVisibilityModel(),
            },
            sorting: { sortModel },
            pinnedColumns: { right: ["actions"] },
          }}
          sx={{
              height: `${gridHeight - 16}px`,
              "& .MuiDataGrid-cell--editing .MuiInput-underline:before": {
                borderBottomColor: "red !important",
              },
            }}
        />
        </ErrorBoundary>
      </Box>
    </Box>
  );
});

declare module "@mui/x-data-grid" {
  interface ToolbarPropsOverrides {
    name: string;
    showArchived: boolean;
    editMode: EditMode;
    searchText: string | undefined;
    onAddRow: () => void;
    onShowArchived: (showArchived: boolean) => void;
    onSearchTextChange: (searchText: string | undefined) => void;
    onMaterialLibrarySelectChange: (newSelections: string[]) => void;
    onFileImport: (files: File[]) => Promise<void>;
  }
}

const EditToolbar = (props: NonNullable<GridSlotsComponentsProps["toolbar"]>) => {
  //console.log("EditToolbar props: ", props);
  const appStore = useAppStore();
  const appSettings = useAppSettings();
  const theme = useTheme();
  const [selectedMaterialLibraries, setSelectedMaterialLibraries] = useState<Co2MaterialLibrary[]>([]);
  console.log(selectedMaterialLibraries);

  const materialLibraries = useCo2MaterialLibraries();
  const clientKey = getClientConfigKey(appStore.applicationSettings);

  useEffect(() => {
    console.log("materialLibraries: ", materialLibraries.data);
    const libraries = materialLibraries.data?.filter((library: Co2MaterialLibrary) => library.materialLibraryType === MaterialLibraryType.PrimarySQL);
    setSelectedMaterialLibraries(libraries.filter((library: Co2MaterialLibrary) => appStore.co2MaterialLibraryIds.includes(library.co2MaterialLibraryId!)));
  }, [materialLibraries.data]);

  const handleSearch = (searchText: string | undefined) => {
    if (props.onSearchTextChange) {
      props.onSearchTextChange(searchText);
    }
  };

  const handleMaterialLibrarySelectChange = async (_event: any, newSelections: Co2MaterialLibrary[]) => {
    setSelectedMaterialLibraries(newSelections);
    const materialLibraryIds = newSelections.map((library: Co2MaterialLibrary) => library.co2MaterialLibraryId!);
    appStore.setMaterialLibraryIds(materialLibraryIds);
    props.onMaterialLibrarySelectChange?.(materialLibraryIds);
  };

  useEffect(() => {
    const materialLibraryIds = selectedMaterialLibraries.map((library) => library.co2MaterialLibraryId!);
    props.onMaterialLibrarySelectChange?.(materialLibraryIds);
  }, [selectedMaterialLibraries]);

  return (
    <GridToolbarContainer
      sx={{
        borderBottom: 1,
        backgroundColor: getBackgroundColor(theme),
        borderColor: getBorderColor(theme),
        fontWeight: "bold",
      }}
    >
      <Grid container spacing={2} sx={{ margin: 0, width: "100%" }}>
        <Grid size={6} sx={{ display: "flex", alignContent: "center", justifyContent: "start", paddingTop: "!important", "& .MuiFormControl-root": { margin: "8px 0" } }}>
          <MultiGridToolbarSearchFilter onChange={handleSearch} value={props.searchText} />
        </Grid>
        <Grid size={6} sx={{ display: "flex", alignContent: "center", justifyContent: "end", paddingTop: "!important", "& .MuiFormControl-root": { margin: "8px 0" } }}>
          <MaterialLibrarySelect
            options={materialLibraries.data}
            value={selectedMaterialLibraries}
            onChange={handleMaterialLibrarySelectChange}
            disabled={props.editMode !== EditMode.ReadOnly}
          />
        </Grid>
        <Grid size={12} sx={{ display: "flex", paddingTop: "!important", "& .MuiFormControl-root": { margin: "8px 0" } }}>
          {clientKey !== "ADStages" && (
            <Tooltip title={i18n.t("materials-tab.grid.add-button.tooltip")}>
              <Button variant="outlined" onClick={() => props.onAddRow?.()} disabled={props.editMode !== EditMode.ReadOnly}>
                {i18n.t("materials-tab.grid.add-button.label")}
              </Button>
            </Tooltip>
          )}
          <Box sx={{ flexGrow: 1 }} />
          <Box>
            {clientKey !== "ADStages" && (
              <FormControlLabel
                control={
                  <Checkbox checked={props.showArchived} onChange={(_, checked) => props.onShowArchived?.(checked)} size="small" disabled={props.editMode !== EditMode.ReadOnly} />
                }
                label={i18n.t("materials-tab.grid.show-archived")}
                slotProps={{
                  typography: { variant: "body2" },
                }}
              />
            )}
          </Box>
          <GridToolbarColumnsButton />
          <GridToolbarFilterButton />
          <GridToolbarImportButton onFileImport={props.onFileImport} />
          <GridToolbarExport csvOptions={{ disableToolbarButton: true }} printOptions={{ disableToolbarButton: true }} />
        </Grid>
      </Grid>
    </GridToolbarContainer>
  );
};

const getBorderColor = (theme: any) => {
  if (theme.palette.mode === "light") {
    return lighten(alpha(theme.palette.background.default, 1), 0.88);
  }
  return darken(alpha(theme.palette.background.default, 1), 0.68);
};

const getBackgroundColor = (theme: Theme) => {
  if (theme.palette.mode === "light") {
    return lighten(alpha(theme.palette.divider, 1), 0.92);
  }
  return darken(alpha(theme.palette.divider, 1), 0.82);
};

export default MaterialGrid;
